import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, FadeIn, Overline, EASE } from "@/components/motion/Reveal";
import { EditorialMarquee } from "@/components/layout/EditorialMarquee";
import { Seo } from "@/components/Seo";
import { SERVICES, VISION_ABOUT, MISSION_ABOUT, IMAGES, HEALTH_CHECK, ADVANTAGES } from "@/constants/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="" className="w-full h-[120%] object-cover object-center" />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-background to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="max-w-7xl mx-auto px-6 lg:px-10 pb-28 pt-48 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <Overline data-testid="hero-overline">Clear Horizon Consulting — Amman, Jordan</Overline>
        </motion.div>

        <MaskedLines
          data-testid="hero-headline"
          className="mt-8 font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl"
          delay={0.35}
          lines={[
            "To redefine how",
            "businesses in Jordan",
            <span key="l3">operate<span className="italic text-primary">.</span></span>,
          ]}
        />

        <motion.p
          data-testid="hero-subline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1 }}
          className="mt-8 max-w-xl text-base lg:text-lg font-light text-muted-foreground leading-relaxed"
        >
          Structured, compliant, and built to scale. Organizational, legal, and financial
          expertise under one roof — for businesses ready to launch, operate, and grow.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <Link
            to="/contact"
            data-testid="hero-contact-cta"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-8 py-4 hover:scale-105 transition-transform duration-300"
          >
            Contact Us <ArrowUpRight className="w-4 h-4" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-services-cta"
            className="inline-flex items-center gap-2 border border-white/20 text-sm text-foreground px-8 py-4 hover:border-primary hover:text-primary transition-colors duration-300"
          >
            Explore Services
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 text-muted-foreground"
      >
        <span className="text-xs tracking-[0.25em] uppercase">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-32 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <FadeIn className="lg:col-span-5">
          <span className="font-serif text-8xl lg:text-9xl font-light text-outline leading-none">01</span>
          <Overline className="block mt-6">Our Vision</Overline>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-7">
          <p data-testid="manifesto-vision" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight text-foreground">
            {VISION_ABOUT}
          </p>
        </FadeIn>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mt-32">
        <FadeIn className="lg:col-span-5 lg:col-start-8 lg:order-2">
          <span className="font-serif text-8xl lg:text-9xl font-light text-outline leading-none">02</span>
          <Overline className="block mt-6">Our Mission</Overline>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-7 lg:order-1 lg:text-right">
          <p data-testid="manifesto-mission" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-snug text-muted-foreground">
            {MISSION_ABOUT}
          </p>
          <Link
            to="/about"
            data-testid="manifesto-about-link"
            className="inline-flex items-center gap-2 mt-10 text-sm text-primary hover:gap-4 transition-[gap] duration-300"
          >
            More about the firm <ArrowUpRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section data-testid="services-grid-section" className="py-32 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
        <div>
          <Overline>What we do</Overline>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-none">
            Three pillars.<br />One accountable partner.
          </h2>
        </div>
        <Link
          to="/services"
          data-testid="services-grid-view-all"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          View all services <ArrowUpRight className="w-4 h-4" />
        </Link>
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5">
        {SERVICES.map((s, i) => (
          <FadeIn
            key={s.slug}
            delay={i * 0.08}
            className="bg-background lg:col-span-4"
          >
            <Link
              to={`/services#${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group relative block p-10 lg:p-14 h-full border border-transparent hover:border-white/10 transition-colors duration-500"
            >
              <span className="absolute top-0 left-0 h-px w-0 bg-primary group-hover:w-full transition-[width] duration-700 ease-out" />
              <span className="font-serif text-5xl font-light text-white/15 group-hover:text-primary/60 transition-colors duration-500">{s.number}</span>
              <h3 className="mt-8 font-serif text-3xl lg:text-4xl font-light text-foreground">{s.title}</h3>
              <p className="mt-4 text-sm lg:text-base font-light text-muted-foreground leading-relaxed max-w-md">{s.tagline}</p>
              <span className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground group-hover:text-primary transition-colors duration-300">
                Discover <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </span>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const WhyChSection = () => (
  <section data-testid="why-ch-section" className="py-32 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="mb-20 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div>
          <Overline>Why CH</Overline>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
            Corporate insiders,<br /><span className="italic text-primary">not textbook consultants.</span>
          </h2>
        </div>
        <p className="max-w-md text-base font-light text-muted-foreground leading-relaxed">
          A practical consulting model built around real business needs.
        </p>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {ADVANTAGES.map((a, i) => (
          <FadeIn key={a.title} delay={i * 0.06} className="bg-background">
            <div data-testid={`advantage-${i + 1}`} className="p-10 lg:p-12 h-full group border border-transparent hover:border-white/10 transition-colors duration-500">
              <span className="font-serif text-3xl font-light text-white/15 group-hover:text-primary/60 transition-colors duration-500">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-6 font-serif text-2xl font-light">{a.title}</h3>
              <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed">{a.text}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const HealthCheckBand = () => (
  <section data-testid="health-check-band" className="py-32 border-t border-white/5 bg-[#0B1322]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <FadeIn className="lg:col-span-7">
        <Overline>Not sure where to start?</Overline>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
          The Business <span className="italic text-primary">Health Check.</span>
        </h2>
        <p className="mt-6 max-w-xl text-base lg:text-lg font-light text-muted-foreground leading-relaxed">{HEALTH_CHECK.description}</p>
      </FadeIn>
      <FadeIn delay={0.15} className="lg:col-span-5 lg:text-right">
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Operations · Legal · Finance</p>
        <Link
          to="/contact"
          data-testid="health-check-band-cta"
          className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-8 py-4 hover:scale-105 transition-transform duration-300"
        >
          Ask about the Health Check <ArrowUpRight className="w-4 h-4" />
        </Link>
      </FadeIn>
    </div>
  </section>
);

const CtaBand = () => (
  <section data-testid="home-cta-band" className="py-32 lg:py-40 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
      <FadeIn>
        <Overline>Ready when you are</Overline>
        <h2 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight leading-tight">
          Bring structure to<br /><span className="italic text-primary">your ambition.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="cta-band-contact-button"
          className="mt-12 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-10 py-5 hover:scale-105 transition-transform duration-300"
        >
          Get in Touch <ArrowUpRight className="w-4 h-4" />
        </Link>
      </FadeIn>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Clear Horizon Consulting — Consulting Firm in Amman, Jordan"
        description="Clear Horizon Consulting in Amman, Jordan — organizational, legal & financial consulting that helps businesses launch, operate, and scale with confidence."
        path="/"
      />
      <h1 className="sr-only">Clear Horizon Consulting — Organizational, Legal & Financial Consulting in Amman, Jordan</h1>
      <Hero />
      <Manifesto />
      <EditorialMarquee />
      <ServicesGrid />
      <WhyChSection />
      <HealthCheckBand />
      <CtaBand />
    </main>
  );
}
